import i18n from '../i18n';

/**
 * 백엔드 오류 응답을 브라우저 언어에 맞는 문구로 바꿉니다.
 *
 * 백엔드의 예외 메시지는 한 언어로 고정된 문장이라 화면에서 번역할 수 없습니다.
 * 그래서 `CodedException` 을 구현한 예외는 응답 본문에 `code` 와 `args` 를 함께
 * 싣고(`backend/.../exception/CodedException.java`), 문구 선택은 이 함수가 합니다.
 *
 * 우선순위는 세 단계입니다.
 *   1. `err.<code>` 번역 문구 — 정상 경로입니다.
 *   2. 백엔드가 보낸 `message` 원문 — 코드가 없는 예외이거나, 구버전 백엔드입니다.
 *   3. 호출부가 지정한 기본 문구 — 본문 자체가 없을 때입니다.
 *
 * 2번이 있어야 배포 순서에 상관없이 화면이 빈 알림을 띄우지 않습니다.
 *
 * `args` 는 위치 인자라 번역 문구에서 `{{0}}`, `{{1}}` 로 참조합니다. 이름 대신
 * 위치를 쓰는 이유는 백엔드가 문구의 구조를 알 필요가 없게 하기 위해서입니다.
 *
 * 컴포넌트 밖(axios 인터셉터 등)에서도 쓰이므로 훅이 아니라 i18n 인스턴스를 직접
 * 참조합니다. `src/i18n/index.js` 는 `src/index.js` 에서 App 보다 먼저 로드됩니다.
 */
export const apiErrorText = (error, fallbackKey) => {
  const fallback = fallbackKey ? i18n.t(fallbackKey) : '';
  const body = errorBody(error);

  if (body?.code) {
    return i18n.t(`err.${body.code}`, {
      ...toPositional(body.args),
      // 화면이 모르는 코드면 백엔드 원문으로 되돌아갑니다. 키 이름이 그대로
      // 노출되는 것보다 낫고, 새 코드가 추가돼도 화면이 깨지지 않습니다.
      defaultValue: body.message || fallback,
    });
  }

  if (body?.message) {
    return body.message;
  }

  return fallback;
};

/**
 * 호출부가 넘긴 값에서 오류 본문을 골라냅니다.
 *
 * axios 오류, fetch 로 직접 파싱한 본문, store/api/action.js 가 되돌려주는 객체가
 * 모두 들어오므로 모양이 한 가지가 아닙니다.
 */
const errorBody = (error) => {
  if (error?.response?.data && typeof error.response.data === 'object') {
    return error.response.data;
  }
  // Error 인스턴스의 message 는 "Network Error" 나 "Request failed with status
  // code 400" 처럼 개발자용 문구라 사용자에게 보여 줄 것이 못 됩니다. 응답 본문이
  // 없는 Error 는 본문 없음으로 취급해 호출부의 기본 문구로 넘깁니다.
  if (error instanceof Error) {
    return null;
  }
  if (error && typeof error === 'object') {
    return error;
  }
  return null;
};

/** `["a", "b"]` → `{ 0: "a", 1: "b" }` */
const toPositional = (args) => {
  if (!Array.isArray(args)) return {};
  return args.reduce((acc, value, index) => {
    acc[index] = value;
    return acc;
  }, {});
};
